// import { defineBoot } from '#q-app/wrappers'
import flagsmith from 'flagsmith'
import { $logger } from './logger'
import { $rollbar } from './rollbar'
import { MINUTE_MS } from '@/utils/time.util'

export const defaultFlags = {
  account_ach_verification_methods: { enabled: true, value: ['micro_transfers'] },
  account_auto_checkout: { enabled: true },
  account_watchlist: { enabled: true, value: { pageSize: 12 } },
  ai_describe_damage: { enabled: true },
  app_version_check: { enabled: true, value: 5 }, // Check every 5 minutes
  auth_sign_in_methods: { enabled: true, value: ['phone'] },
  blacklist_slugs: {
    enabled: true,
    value: ['home-page', 'retailer-setup-page', 'app-how-it-works', 'app-what-is-peg', 'app-how-to-start-a-return'],
  },
  checkout_payment_gateway: { enabled: true, value: 'braintree' },
  checkout_payment_methods: { enabled: true, value: ['card', 'ach'] },
  contact_info: { enabled: false, value: null }, // PEG-536: support contacts now sourced from API via appConfig store
  demo_mode: { enabled: false }, // when enabled, hides internal/test-only UI (InternalTools, env test banner)
  fulfillment_pickup: { enabled: true },
  marketing_sms_excluded_states: { enabled: true, value: ['TX'] },
  maintenance_mode: { enabled: false, value: null },
  original_price_prop: { enabled: true, value: 'retailPrice' },
  partner_exp_whitelist: { enabled: true, value: ['rush-market'] },
  result_list: { enabled: true, value: { defaultPageSize: 48, defaultSort: 'Newest' } },
  retailer_admin_pickup: { enabled: false },
}

// Initialize Flagsmith, then fetch flags and start polling once init resolves.
// NOTE: getFlags() must not be called before init() completes — doing so puts
// the SDK in an invalid state and triggers the onError callback.
flagsmith
  .init({
    environmentID: import.meta.env.VITE_FLAGSMITH_ENV_ID,
    // enableLogs: import.meta.env.DEV,
    enableAnalytics: true,
    defaultFlags,
    cacheFlags: true, // Cache flags locally, and serve from cache until updated
    onChange: (oldFlags, params) => {
      if (!oldFlags) return // skip if no old flags (initial load)

      $logger.debug('FEATURE FLAGS CHANGED', oldFlags, params)
      // Update reactive state when flags change
      // This is also called directly in useFeatureFlagStore on init
      useFeatureFlagStore().initFeatureFlags()
    },
    onError: error => {
      $rollbar.error(
        `Error initializing Flagsmith: message=${error?.message || String(error)}, name=${error?.name || 'unknown'}, type=${typeof error}`,
        error
      )
    },
  })
  .then(() => {
    // Fetch flags and start polling only after init has fully resolved
    flagsmith
      .getFlags()
      .catch(error => {
        // Initial getFlags() failures are non-fatal: defaultFlags + cached flags
        // are already loaded, and startListening() continues polling regardless.
        // Network-class failures (offline, ad/privacy blockers, transient blips)
        // are expected noise on public anonymous traffic — only report genuine errors.
        const isNetworkError = /failed to fetch|networkerror|load failed|network request failed/i.test(error?.message || '')
        if (!isNetworkError) {
          $rollbar.error('Error fetching initial Flagsmith flags:', error)
        }
      })
      .finally(() => {
        flagsmith.startListening(MINUTE_MS * 5)
      })
  })

export const $flagsmith = flagsmith
